import useOffer from 'hooks/Offers/useOffer'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { getBedbankItemViewTotals } from 'checkout/lib/utils/bedbank/view'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { useEffect, useMemo } from 'react'
import { isAccommodationCheckInDatePast } from 'lib/datetime/dateUtils'
import { getAccommodationPropertyTimeZone } from 'lib/datetime/accommodationPropertyTimeZone'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { matchBedbankRateOrItem } from 'lib/hotels/bedbankUtils'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'

export type UserMultiCartBedbankHotelItemData = {
  offer: App.BedbankOffer
  loaded: true
  loading: false
  error: any
  package: App.BedbankPackage
  itemTotals: App.Checkout.ItemViewTotals
  isPast: boolean
  isSoldOut: boolean
  isBookable: boolean
  rate: App.BedbankRate | undefined
} | {
  offer: App.BedbankOffer | undefined
  loaded: false
  loading: boolean
  error: any
  package: App.BedbankPackage | undefined
  itemTotals: App.Checkout.ItemViewTotals | undefined
  isPast: boolean
  isSoldOut: boolean
  isBookable: boolean
  rate: App.BedbankRate | undefined
}

function useUserMultiCartBedbankHotelItemData(multiCartItem: App.MultiCartBedbankHotelItem, saveForLaterIfUnavailable = true): UserMultiCartBedbankHotelItemData {
  const item = multiCartItem.item as App.Checkout.BedbankHotelItem
  const multiCartActorRef = MultiCartContext.useActorRef()
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()

  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)

  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)

  const [offer, offerLoading, offerError] = useOffer<App.BedbankOffer>(multiCartItem.offerId)

  // Get multi-cart items from XState instead of Redux
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const accommodationItems = useMemo(() => {
    if (cartItemsState.status !== 'loaded' || !activeCartId) return []
    const cartItems = Object.values(cartItemsState.data[activeCartId] || {})
    return cartItems
      .filter(multiCartItem => multiCartItem.state === 'active')
      .map(multiCartItem => multiCartItem.item)
      .filter(item => isBedbankItem(item))
  }, [cartItemsState, activeCartId])

  let itemGroup: Array<App.Checkout.BedbankHotelItem>
  /**
   * For bundles, dont group sibling cart items since each bundled item is its own per-occupancy rate lookup
   * plus svc-acconnodation currently does not return bundles for combined occupancy
   */
  if (multiCartItem.state === 'save_for_later' || item.isAccomBundle) {
    itemGroup = [item]
  } else {
    // Filter accommodationItems - they're already filtered to active items from XState
    itemGroup = accommodationItems
      .filter(bedbankItem => !bedbankItem.isAccomBundle)
      .filter(bedbankItem => item.offerId === multiCartItem.offerId &&
        item.checkIn === multiCartItem.checkIn &&
        item.checkOut === multiCartItem.checkOut &&
        matchBedbankRateOrItem(item, bedbankItem),
      )
  }
  const rateKey = getBedbankRateKey(itemGroup.map(item => item.occupancy), multiCartItem.checkIn, multiCartItem.checkOut)
  const rate = useMemo<App.BedbankRate | undefined>(() => {
    return bedbankOfferRates[item.offerId]?.[rateKey]?.find(r => r.id === multiCartItem.roomRateId && r.isFlightBundle === multiCartItem.isFlightBundle)
  }, [bedbankOfferRates, item.offerId, multiCartItem.roomRateId, multiCartItem.isFlightBundle, rateKey])

  const {
    rates,
    ratesLoading,
    luxPlusLPPEnabled,
  } = useAppSelector(state => ({
    rates: state.offer.bedbankOfferRates[multiCartItem.offerId]?.[rateKey],
    ratesLoading: state.offer.bedbankOfferRatesLoading[multiCartItem.offerId]?.[rateKey],
    luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
  }), shallowEqual)

  let bedbankOfferRate = rates?.find(rate => rate.id === multiCartItem.roomRateId && rate.isFlightBundle === multiCartItem.isFlightBundle)
  let matchedNewRateId = false
  if (!bedbankOfferRate) {
    bedbankOfferRate = rates?.find(rate => matchBedbankRateOrItem(item, rate))
    matchedNewRateId = !!bedbankOfferRate
  }
  const pkg = bedbankOfferRate?.room

  // Eslint is wrong. `ratesLoading` is false when the rates are loaded, and undefined when they haven't been loaded yet, and we need to make that distinction
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
  const isSoldOut = ratesLoading === false && !bedbankOfferRate
  const propertyTimeZone = getAccommodationPropertyTimeZone(offer)
  const isOfferPending = !offer && !offerError
  const isPast = propertyTimeZone ? isAccommodationCheckInDatePast(
    multiCartItem.checkIn,
    { timeZone: propertyTimeZone },
  ) : false
  const isBookable = !isPast && !isSoldOut && !offerError
  const loading = !!(offerLoading || ratesLoading || isOfferPending)

  useEffect(() => {
    if (!matchedNewRateId) return
    if (!bedbankOfferRate) return

    // this condition is important to avoid infinite loop of updates because of useEffect
    if (item.roomRateId === bedbankOfferRate.id && item.rateGroupId === bedbankOfferRate.groupId && item.bedGroupId === bedbankOfferRate.bedGroups[0].id && item.isFlightBundle === bedbankOfferRate.isFlightBundle) {
      return
    }

    // Update item with the newly matched rate
    const updatedItem: App.Checkout.BedbankHotelItem = {
      ...item,
      roomRateId: bedbankOfferRate.id,
      rateGroupId: bedbankOfferRate.groupId,
      bedGroupId: bedbankOfferRate.bedGroups[0].id,
      isFlightBundle: bedbankOfferRate.isFlightBundle,
    }
    const updatedMultiCartItem: App.MultiCartBedbankHotelItem = {
      ...multiCartItem,
      item: updatedItem,
    }
    multiCartActorRef.send({
      type: 'update cart item',
      cartId: activeCartId!,
      multiCartItem: updatedMultiCartItem,
    })
  }, [bedbankOfferRate, matchedNewRateId, multiCartActorRef, activeCartId, item, multiCartItem])

  useEffect(() => {
    if (saveForLaterIfUnavailable && !isBookable && !loading && multiCartItem.state === 'active') {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [multiCartItem.id],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [isBookable, loading, multiCartItem.id, saveForLaterIfUnavailable, multiCartItem.state, activeCartId, multiCartReviewActorRef])

  if (offer && pkg && bedbankOfferRate) {
    const itemTotals = getBedbankItemViewTotals(bedbankOfferRate, multiCartItem.occupancy, luxPlusLPPEnabled)

    return {
      offer,
      loaded: true,
      loading: false,
      error: offerError,
      package: pkg,
      itemTotals,
      isPast,
      isSoldOut,
      isBookable,
      rate,
    }
  }

  return {
    offer,
    loaded: false,
    loading,
    error: offerError,
    package: pkg,
    itemTotals: undefined,
    isPast,
    isSoldOut,
    isBookable,
    rate,
  }
}

export default useUserMultiCartBedbankHotelItemData
